document.addEventListener('DOMContentLoaded', function () {
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) {
    // No animation: the row stays a plain, manually scrollable list with
    // each product shown exactly once.
    return;
  }

  // A row is "comfortably longer than the screen" when its products are at
  // least this many times wider than the visible area. In that case nothing
  // is duplicated: the row scrolls through the real list and rewinds.
  var SCAN_RATIO = 1.25;
  // Speed in pixels per second, independent of the screen's refresh rate.
  var SPEED = 60;
  // After a pause (tab in the background) never jump ahead by more than this.
  var MAX_FRAME_MS = 50;

  // Appends one copy of every original card to the end of the row. The
  // copies are hidden from screen readers and keyboard focus: they only
  // exist to fill the screen when the real products are too few for it.
  function appendCopy(row, originals, markFirst) {
    originals.forEach(function (card, index) {
      var clone = card.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      clone.setAttribute('data-clone', 'true');

      if (markFirst && index === 0) {
        clone.setAttribute('data-dup', 'true');
      }

      row.appendChild(clone);
    });
  }

  function clearClones(row) {
    Array.prototype.slice.call(row.querySelectorAll('[data-clone]')).forEach(function (clone) {
      row.removeChild(clone);
    });
  }

  function setupRow(row) {
    if (row.hasAttribute('data-autoscroll-ready')) {
      return;
    }

    var originals = Array.prototype.slice.call(row.children);
    if (originals.length === 0) {
      return;
    }
    row.setAttribute('data-autoscroll-ready', 'true');

    var isPremium = row.closest('.product-row--premium') !== null;
    var direction = isPremium ? -1 : 1;
    var isPaused = false;
    var resumeTimer = null;
    var isResetting = false;
    var isRewinding = false;
    var rewindTarget = 0;
    var rewindDeadline = 0;
    var started = false;
    var lastWidth = -1;

    // "scan": nothing is duplicated, the row goes from one end to the other,
    // waits and rewinds. "loop": just enough copies are added to fill the
    // screen and the row wraps around without a visible jump.
    var mode = 'scan';
    // Loop mode: distance between a card and its copy. Scan mode: unused.
    var period = 0;
    var maxScroll = 0;

    // The position is kept here as a decimal number and only written to the
    // browser, as a whole number of pixels. It is never read back from
    // row.scrollLeft while the row moves on its own, because phone browsers
    // round scrollLeft: reading back "0" after setting "0.6" would make a row
    // that moves forward stand still forever. It is read again only after the
    // visitor has touched or scrolled the row (syncPosition).
    // Whole pixels on purpose: with fractional positions the browser draws
    // the text at a slightly different sub-pixel offset on every frame and
    // the letters shimmer; whole pixels keep every letter drawn the same way.
    var position = 0;
    var lastWritten = -1;
    var lastFrameTime = null;

    function syncPosition() {
      position = row.scrollLeft;
      lastWritten = Math.round(position);
    }

    function setPosition(value) {
      position = value;
      var whole = Math.round(value);
      if (whole !== lastWritten) {
        lastWritten = whole;
        row.scrollLeft = whole;
      }
    }

    // Decides, from the real widths, whether the row needs copies, and sets
    // everything up from the start. It runs again whenever the width of the
    // row changes (rotation, resize), because the answer can change.
    function layout() {
      var client = row.clientWidth;
      if (client === 0) {
        return;
      }

      lastWidth = client;
      clearClones(row);
      started = false;
      isResetting = false;
      isRewinding = false;

      var first = originals[0];
      var last = originals[originals.length - 1];
      var cardsWidth = last.offsetLeft + last.offsetWidth - first.offsetLeft;
      var gap = originals.length > 1 ? originals[1].offsetLeft - first.offsetLeft - first.offsetWidth : 0;
      period = cardsWidth + gap;

      if (cardsWidth > client * SCAN_RATIO) {
        mode = 'scan';
        maxScroll = row.scrollWidth - client;

        if (maxScroll > 0) {
          started = true;
          setPosition(isPremium ? maxScroll : 0);
        }
        return;
      }

      mode = 'loop';
      // Enough copies so that, at the furthest point of the loop, the screen
      // is still full of cards (the first copy is the original row).
      var copies = Math.max(2, Math.ceil((client + period + gap) / period));
      for (var i = 1; i < copies; i++) {
        appendCopy(row, originals, i === 1);
      }

      started = true;
      setPosition(isPremium ? period : 0);
    }

    function onResize() {
      if (row.clientWidth !== lastWidth) {
        layout();
      } else if (mode === 'scan') {
        maxScroll = row.scrollWidth - row.clientWidth;
      }
    }

    function stepLoop(distance) {
      var next = position + direction * distance;
      var willWrap = direction === 1 ? next >= period : next <= 0;

      if (willWrap) {
        // A copy starts exactly one period after its original, so moving by
        // one period shows the same picture: the wrap is instant and invisible.
        setPosition(direction === 1 ? next - period : next + period);
      } else {
        setPosition(next);
      }
    }

    function stepScan(distance) {
      if (maxScroll <= 0) {
        return;
      }

      var next = position + direction * distance;
      var atEnd = direction === 1 ? next >= maxScroll : next <= 0;

      if (atEnd) {
        // Wait a moment at the end of the list, then rewind smoothly to the
        // other end and start again. The row stays frozen (isResetting) until
        // the browser has really arrived: see step().
        isResetting = true;
        setTimeout(function () {
          rewindTarget = direction === 1 ? 0 : maxScroll;
          rewindDeadline = performance.now() + 4000;
          isRewinding = true;
          row.scrollTo({ left: rewindTarget, behavior: 'smooth' });
        }, 1500);
      } else {
        setPosition(next);
      }
    }

    function step(timestamp) {
      // The section was removed from the page (for example in the theme
      // editor): stop for good instead of looping on a detached element.
      if (!row.isConnected) {
        return;
      }

      // The distance to move depends on the time that passed since the last
      // frame, so the speed is the same on 60 Hz and 120 Hz screens.
      var elapsed = lastFrameTime === null ? 0 : Math.min(timestamp - lastFrameTime, MAX_FRAME_MS);
      lastFrameTime = timestamp;

      // The smooth rewind is done when the row has reached its target (or
      // after a safety limit). Only then does the row start moving again, so
      // the script never fights the browser's own animation.
      if (isRewinding && (Math.abs(row.scrollLeft - rewindTarget) <= 1 || timestamp > rewindDeadline)) {
        isRewinding = false;
        isResetting = false;
        syncPosition();
      }

      if (started && !isPaused && !isResetting) {
        var distance = SPEED * elapsed / 1000;
        if (mode === 'loop') {
          stepLoop(distance);
        } else {
          stepScan(distance);
        }
      }
      requestAnimationFrame(step);
    }

    function pause() {
      isPaused = true;
      clearTimeout(resumeTimer);
    }

    function resume() {
      // The visitor may have scrolled the row by hand while it was paused:
      // continue from where it is now, not from where it was.
      syncPosition();
      isPaused = false;
    }

    function pauseWithResume() {
      isPaused = true;
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(resume, 2000);
    }

    // Touch screens fake a mouseenter after a tap and never send the matching
    // mouseleave, which would freeze the row forever. Hover pause is therefore
    // only wired on devices that really have hover (mouse, trackpad).
    if (window.matchMedia('(hover: hover)').matches) {
      row.addEventListener('mouseenter', pause);
      row.addEventListener('mouseleave', resume);
    }
    row.addEventListener('touchstart', pause, { passive: true });
    row.addEventListener('touchend', pauseWithResume);
    row.addEventListener('wheel', pauseWithResume, { passive: true });

    // Fires once right after observe() and then whenever the row's box
    // changes. Card widths are fixed in the CSS, so images loading late do
    // not move anything: only a real change of the row's width matters.
    var resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(row);

    requestAnimationFrame(step);
  }

  document.querySelectorAll('[data-autoscroll]').forEach(setupRow);

  // Theme editor: a section that is edited is drawn again, so its rows have to be set up again.
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-autoscroll]').forEach(setupRow);
  });
});
